/**
 * WhatsApp Service
 * Handles sending WhatsApp messages via Firebase Functions or direct API
 */

import { whatsappConfig, configHelpers, apiConfig, firebaseConfig } from '../config/app';
import {
  buildMotBookedConfirmationTemplatePayload,
  isMotBookedConfirmationTemplateConfigured,
  normalizePhoneForWhatsApp,
} from '../utils/whatsappCustomerUpdates';
import { formatMotTimeSlotDisplay } from '../utils/helpers';

/**
 * Get the Firebase Functions URL for WhatsApp function
 */
const getWhatsAppFunctionUrl = () => {
  return whatsappConfig.getFunctionUrl();
};

/**
 * Format phone number for WhatsApp
 * Removes spaces, dashes, and ensures it starts with country code
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
const formatPhoneNumber = (phone) => {
  return configHelpers.formatPhoneNumber(phone);
};

/**
 * Send WhatsApp message (text, optional media by HTTPS link, or Meta template).
 * @param {string} phone - Recipient phone number
 * @param {string} [message] - Text body (optional if media or template provided)
 * @param {{ media?: Array<{type:string,link:string,caption?:string,filename?:string}>, template?: { name: string, languageCode?: string, components?: unknown[] } }} [options]
 * @returns {Promise<Object>} Response from API
 */
export const sendWhatsApp = async (phone, message, options = {}) => {
  const { media, template, companyId } = options || {};
  const msgTrim = message != null ? String(message).trim() : '';

  if (!phone) {
    throw new Error('Phone number is required');
  }
  if (!template && !msgTrim && (!media || media.length === 0)) {
    throw new Error('Message, media, or template is required');
  }

  const formattedPhone =
    normalizePhoneForWhatsApp(phone) || formatPhoneNumber(phone);

  if (!formattedPhone) {
    throw new Error('Invalid phone number format');
  }

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.whatsapp * (media?.length ? 3 : 1));

    const functionUrl = getWhatsAppFunctionUrl();
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone: formattedPhone,
        message: msgTrim,
        ...(companyId ? { companyId } : {}),
        ...(media && media.length > 0 ? { media } : {}),
        ...(template && template.name ? { template } : {}),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      let errorData = {};
      try {
        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const text = (await response.text()).trim();
          errorData = text ? { message: text.slice(0, 400) } : {};
        }
      } catch (_) {
        errorData = {};
      }

      const serverMsg = String(errorData.message || errorData.error || '').trim();

      if (response.status === 401 || response.status === 403) {
        const projectId = firebaseConfig.projectId || 'YOUR_PROJECT_ID';
        const region = firebaseConfig.region || 'us-central1';
        throw new Error(
          `WhatsApp endpoint returned HTTP ${response.status} (Google blocked the request before your code ran). ` +
            'Grant public invoke on the Gen 2 Cloud Run service, e.g. ' +
            `gcloud run services add-iam-policy-binding sendwhatsapp --region=${region} ` +
            `--member="allUsers" --role="roles/run.invoker" --project=${projectId} ` +
            `Or: ./scripts/set-functions-iam.sh ${projectId} ${region}. ` +
            (serverMsg ? `Response: ${serverMsg.slice(0, 180)}` : '')
        );
      }

      if (response.status === 400) {
        throw new Error(errorData.message || errorData.error || 'Invalid request. Please check phone number and message.');
      }

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('WhatsApp sending timed out. Please try again.');
    }
    if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Cannot connect to WhatsApp service. Please check your internet connection.');
    }
    throw error;
  }
};

/**
 * Send WhatsApp broadcast to multiple recipients
 * @param {Array<{phone: string, message: string}>} recipients - Array of recipients with phone and personalized message
 * @param {Function} onProgress - Optional progress callback (sent, total)
 * @returns {Promise<Object>} Summary of sent messages
 */
export const sendWhatsAppBroadcast = async (recipients, onProgress) => {
  if (!recipients || recipients.length === 0) {
    throw new Error('At least one recipient is required');
  }

  const results = {
    sent: 0,
    failed: 0,
    errors: []
  };

  for (let i = 0; i < recipients.length; i++) {
    const recipient = recipients[i];
    
    try {
      await sendWhatsApp(recipient.phone, recipient.message);
      results.sent++;
      
      if (onProgress) {
        onProgress(i + 1, recipients.length);
      }
      
      // Small delay to avoid rate limiting
      if (i < recipients.length - 1) {
        await new Promise(resolve => setTimeout(resolve, whatsappConfig.broadcastDelay));
      }
    } catch (error) {
      results.failed++;
      results.errors.push({
        phone: recipient.phone,
        error: error.message
      });
      
      // Continue sending to other recipients even if one fails
      console.error(`Failed to send WhatsApp to ${recipient.phone}:`, error);
    }
  }

  return results;
};

/**
 * Send MOT booked-in confirmation template. Never throws for skip cases
 * (no template, no mobile, no time slot). Throws only if Meta send fails.
 * @returns {Promise<{sent: boolean, skipped?: string}>}
 */
export const sendMotBookedConfirmationWhatsApp = async ({
  contact,
  customerName,
  companyName,
  vehicleReg,
  vehicleName,
  timeSlot,
  dropOffDate,
  companyId,
} = {}) => {
  if (!isMotBookedConfirmationTemplateConfigured()) {
    return { sent: false, skipped: 'not_configured' };
  }
  const phoneRaw = String(contact || '').trim();
  if (!phoneRaw || !normalizePhoneForWhatsApp(phoneRaw)) {
    return { sent: false, skipped: 'no_phone' };
  }
  if (!formatMotTimeSlotDisplay(timeSlot, dropOffDate)) {
    return { sent: false, skipped: 'no_slot' };
  }
  const template = buildMotBookedConfirmationTemplatePayload({
    customerName,
    companyName,
    vehicleReg,
    vehicleName,
    timeSlot,
    dropOffDate,
  });
  if (!template) {
    return { sent: false, skipped: 'not_configured' };
  }
  await sendWhatsApp(phoneRaw, '', { template, companyId });
  return { sent: true };
};

const whatsappApi = {
  sendWhatsApp,
  sendWhatsAppBroadcast,
  sendMotBookedConfirmationWhatsApp,
  formatPhoneNumber
};

export default whatsappApi;

